import { Button } from 'components/atoms';
import ExternalIdCard from 'components/molecules/card/external-id/ExternalIdCard';
import ViewHint from 'components/molecules/hint/ViewHint';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiLink, PiPaperPlaneTilt } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { IExternalId } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

export const externalInitialValues: IExternalId = {
  id: '',
  type: '',
  link: '',
};

type ParentType = 'ARTIST' | 'TRACK' | 'VIDEO' | 'MUSIC_PRODUCT_DIGITAL' | 'MUSIC_PRODUCT_PHYSICAL';

interface IExternalIdTabProps {
  propertyName?: 'artists';
  parent: ParentType;
}

const NAVIGATION_MAP = {
  MUSIC_PRODUCT_DIGITAL: {
    storeKey: 'musicProductDigitalForm' as const,
    basePath: 'music-product-digital',
    back: 'titles',
    hint: 'music-product-digital-external-id',
    next: 'delivery',
    isLast: true,
    nextIcon: undefined,
  },
  MUSIC_PRODUCT_PHYSICAL: {
    storeKey: 'musicProductPhysicalForm' as const,
    basePath: 'music-product-physical',
    back: 'titles',
    next: undefined,
    hint: 'music-product-physical-external-id',
    isLast: true,
    nextIcon: <PiPaperPlaneTilt />,
  },
  TRACK: {
    storeKey: 'trackForm' as const,
    basePath: 'track',
    back: 'titles',
    next: undefined,
    hint: 'track-external-id',
    isLast: true,
    nextIcon: <PiPaperPlaneTilt />,
  },
  VIDEO: {
    storeKey: 'videoForm' as const,
    basePath: 'video',
    back: 'titles',
    next: undefined,
    hint: 'video-external-id',
    isLast: true,
    nextIcon: <PiPaperPlaneTilt />,
  },
  ARTIST: {
    storeKey: undefined,
    basePath: 'artist',
    back: 'data',
    next: undefined,
    hint: 'artist-external-id',
    isLast: true,
    nextIcon: <PiPaperPlaneTilt />,
  },
};

const ExternalIdTab = <Form extends Record<'externalIds', IExternalId[]> & Record<'id', string>>({
  parent,
}: IExternalIdTabProps) => {
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();

  const { t } = useTranslation();

  const {
    setForm,
    musicProductDigitalForm,
    musicProductPhysicalForm,
    trackForm,
    videoForm,
    artistForm,
  } = useStoreState();

  const navigate = useNavigate();

  const config = NAVIGATION_MAP[parent];

  const handleNavigate = (direction: 'next' | 'back') => {
    if (!config) return;

    if (config.storeKey) {
      setForm(config.storeKey, values as any);
    }

    const subPath = direction === 'back' ? config.back : (config.next ?? '');
    const action = values.id ? `edit/${values.id}` : 'create';

    navigate(`/${config.basePath}/${action}/${subPath}`);
  };

  const lastName = useMemo(() => {
    if (
      parent === 'ARTIST' &&
      (musicProductDigitalForm || musicProductPhysicalForm || trackForm || videoForm)
    ) {
      return 'save-and-back';
    }
    if (
      (parent === 'TRACK' || parent === 'VIDEO') &&
      (musicProductDigitalForm || musicProductPhysicalForm)
    ) {
      return 'save-and-back';
    }

    if (parent === 'MUSIC_PRODUCT_DIGITAL') {
      return 'delivery';
    }

    return 'send-to-verification';
  }, [musicProductDigitalForm, musicProductPhysicalForm, trackForm, videoForm, artistForm, parent]);

  return (
    <StepsTab
      isLast={config.isLast}
      nextName={config.isLast ? lastName : config?.next}
      nextIcon={config.isLast ? config?.nextIcon : undefined}
      backName={config?.back}
      onNext={() => !config.isLast && handleNavigate('next')}
      onBack={() => handleNavigate('back')}
    >
      <ViewHint hintId={config?.hint} />
      {!status?.displayMode && (
        <Button
          fullWidth
          variant={BUTTON_VARIANT.white}
          onClick={() =>
            setFieldValue('externalIds', [externalInitialValues, ...values.externalIds])
          }
          size="large"
        >
          <Button.Icon icon={<PiLink />} />
          <Button.Text text={t('button.add-service-id')} />
        </Button>
      )}
      <ListWrapper
        isLoading={status?.isLoading}
        items={values.externalIds}
        item={(external, index) => (
          <ExternalIdCard
            key={index}
            index={index}
            externalId={external}
            displayMode={status?.displayMode}
            onRemove={() =>
              setFieldValue(
                'externalIds',
                values.externalIds.filter((_, externalIndex) => index !== externalIndex),
              )
            }
          />
        )}
      />
    </StepsTab>
  );
};

export default ExternalIdTab;
